import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Download, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMember } from "@/contexts/MemberAuthContext";
import NotificationsBell from "@/components/NotificationsBell";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Music", to: "/music" },
  { label: "Videos", to: "/videos" },
  { label: "Store", to: "/store" },
  { label: "Vault", to: "/vault" },
  { label: "Leaderboards", to: "/leaderboards" },
  { label: "Luxifer Picks", to: "/affiliate" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { member } = useMember();

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-2xl border-b border-white/10"
    >
      <nav className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="font-heading text-2xl tracking-wider text-[#D4AF37] hover:text-white transition-colors duration-300 whitespace-nowrap flex-shrink-0"
        >
          LUXIFER 6
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`text-sm uppercase tracking-[0.2em] transition-colors duration-300 ${
                location.pathname === link.to
                  ? "text-[#D4AF37]"
                  : "text-white/60 hover:text-white"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/my-downloads"
            data-testid="nav-my-downloads"
            className={`flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
              location.pathname === "/my-downloads"
                ? "text-[#D4AF37]"
                : "text-white/50 hover:text-[#D4AF37]"
            }`}
            title="My Downloads"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden lg:inline">Downloads</span>
          </Link>
          {member ? (
            <>
              <NotificationsBell />
              <Link
                to="/account"
                data-testid="nav-account"
                className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-white"
                title="My Account"
              >
                <User className="w-3.5 h-3.5" />
                <span className="hidden lg:inline">{member.username}</span>
              </Link>
            </>
          ) : (
            <Link
              to="/join"
              data-testid="nav-join"
              className="text-xs uppercase tracking-[0.2em] px-4 py-1.5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors"
            >
              Join
            </Link>
          )}
        </div>

        {/* Mobile */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              data-testid="mobile-menu-toggle"
              className="text-white hover:text-[#D4AF37] hover:bg-white/5"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="bg-[#0A0A0A] border-white/10 w-72"
          >
            <SheetTitle className="font-heading text-2xl text-[#D4AF37] tracking-wider mb-2">
              LUXIFER 6
            </SheetTitle>
            <p className="sr-only">Navigation menu</p>
            <div className="flex flex-col gap-1 mt-6">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`text-base uppercase tracking-[0.15em] py-3 px-2 border-b border-white/5 transition-colors ${
                    location.pathname === link.to
                      ? "text-[#D4AF37]"
                      : "text-white/60 hover:text-white"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/my-downloads"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-my-downloads"
                className={`flex items-center gap-2 text-base uppercase tracking-[0.15em] py-3 px-2 border-b border-white/5 transition-colors ${
                  location.pathname === "/my-downloads"
                    ? "text-[#D4AF37]"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <Download className="w-4 h-4" /> My Downloads
              </Link>
              <Link
                to={member ? "/account" : "/join"}
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-account"
                className="flex items-center gap-2 text-base uppercase tracking-[0.15em] py-3 px-2 text-[#D4AF37] hover:text-white transition-colors"
              >
                <User className="w-4 h-4" /> {member ? "My Account" : "Join"}
              </Link>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
}
